import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { BookOpen, Info, KeyRound } from 'lucide-react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Sidebar, type SidebarGroup } from '@/components/shell/Sidebar';
import { useSetBreadcrumbs } from '@/components/shell/ShellContext';
import { toast } from '@/hooks/useToast';

import { usePermissionVocabulary, useTenant, useTenantRole, useUpdateTenantRole } from '../api';
import { roleDisplayLabel } from '../roleLabel';
import { PermissionVocabularyReference } from './PermissionVocabularyReference';
import { RolePermissionsEditor } from './RolePermissionsEditor';

/**
 * Full role record page (PTV2-1444). Reached by clicking a role in a tenant's Roles tab, it wears the
 * same chrome as the patient / family / tenant records: a left contextual sidebar whose sections —
 * Permissions, Details, Reference — switch the content on the URL hash. The role's internal name is
 * shown as its identifier on the Details section — the one place a user sees the raw name; everywhere
 * else uses the human label.
 */
export function TenantRoleDetailView(): ReactElement {
  const { t } = useTranslation();
  const { uuid = '', guid = '' } = useParams<{ uuid: string; guid: string }>();
  const { hash } = useLocation();
  const rd = 'routes.platform.detail.roleDetail';

  const tenantQuery = useTenant(uuid.length > 0 ? uuid : null);
  const roleQuery = useTenantRole(uuid, guid);
  const vocabularyQuery = usePermissionVocabulary();
  const updateRole = useUpdateTenantRole();

  const tenant = tenantQuery.data ?? null;
  const role = roleQuery.data ?? null;
  const activeSection = hash === '#details' ? 'details' : hash === '#reference' ? 'reference' : 'permissions';

  const [description, setDescription] = useState('');
  useEffect(() => {
    if (role !== null) {
      setDescription(role.description);
    }
  }, [guid, role]);

  const tenantLabel = tenant === null ? uuid : tenant.label.length > 0 ? tenant.label : tenant.name;
  useSetBreadcrumbs([
    { label: t('shell.topNav.admin'), to: '/platform' },
    { label: t('routes.platform.sidebar.tenants'), to: '/platform/tenants' },
    { label: tenantLabel, to: `/platform/tenants/${uuid}` },
    { label: t('routes.platform.detail.sidebar.roles'), to: `/platform/tenants/${uuid}#roles` },
    ...(role === null ? [] : [{ label: roleDisplayLabel(role.name) }]),
  ]);

  const base = `/platform/tenants/${uuid}/roles/${guid}`;
  const sidebarGroups = useMemo<readonly SidebarGroup[]>(
    () => [
      {
        key: 'role',
        heading: t(`${rd}.sidebar.heading`),
        items: [
          {
            key: 'permissions',
            label: t(`${rd}.sidebar.permissions`),
            to: `${base}#permissions`,
            icon: <KeyRound className="h-4 w-4" aria-hidden="true" />,
            defaultActive: true,
          },
          {
            key: 'details',
            label: t(`${rd}.sidebar.details`),
            to: `${base}#details`,
            icon: <Info className="h-4 w-4" aria-hidden="true" />,
          },
          {
            key: 'reference',
            label: t(`${rd}.sidebar.reference`),
            to: `${base}#reference`,
            icon: <BookOpen className="h-4 w-4" aria-hidden="true" />,
          },
        ],
      },
    ],
    [t, base],
  );

  return (
    <div className="flex h-full">
      <Sidebar groups={sidebarGroups} />
      <main className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-clip">
        {role === null ? (
          <div className="flex-1 overflow-y-auto bg-background px-8 py-6">
            {roleQuery.isPending && <Skeleton className="h-8 w-64" />}
            {roleQuery.isError &&
              (roleQuery.error instanceof ApiError && roleQuery.error.status === 404 ? (
                <p role="alert" className="text-sm text-red-600">{t(`${rd}.notFound`)}</p>
              ) : (
                <div className="max-w-xl">
                  <FetchErrorCallout
                    error={roleQuery.error}
                    heading={t(`${rd}.error.heading`)}
                    message={t(`${rd}.error.message`)}
                    codeLabel={t('routes.platform.detail.roles.error.codeLabel')}
                    onRetry={() => { roleQuery.refetch().catch(() => undefined); }}
                    retryLabel={t('routes.platform.detail.roles.error.retry')}
                  />
                </div>
              ))}
          </div>
        ) : (
          <>
            <header className="border-b border-border bg-card px-8 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-semibold text-foreground">{roleDisplayLabel(role.name)}</h1>
                {role.builtIn && <Badge variant="outline">{t(`${rd}.builtInBadge`)}</Badge>}
                <code className="ml-1 font-mono text-xs text-muted-foreground">{role.name}</code>
              </div>
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto bg-background">
              <div className="mx-auto max-w-[1100px] px-8 py-6">
                {activeSection === 'permissions' && (
                  <section className="space-y-3">
                    <h2 className="text-sm font-semibold text-foreground">{t(`${rd}.sidebar.permissions`)}</h2>
                    {vocabularyQuery.isPending && (
                      <Skeleton className="h-40 w-full rounded-md" aria-hidden="true" />
                    )}
                    {vocabularyQuery.isError && (
                      <FetchErrorCallout
                        error={vocabularyQuery.error}
                        heading={t('routes.platform.detail.roles.permissions.loadError.heading')}
                        message={t('routes.platform.detail.roles.permissions.loadError.message')}
                        codeLabel={t('routes.platform.detail.roles.error.codeLabel')}
                        onRetry={() => { vocabularyQuery.refetch().catch(() => undefined); }}
                        retryLabel={t('routes.platform.detail.roles.error.retry')}
                      />
                    )}
                    {vocabularyQuery.isSuccess && (
                      <RolePermissionsEditor
                        tenantUuid={uuid}
                        roleGuid={guid}
                        vocabulary={vocabularyQuery.data}
                      />
                    )}
                  </section>
                )}

                {activeSection === 'details' && (
                  <section className="max-w-2xl space-y-4">
                    <h2 className="text-sm font-semibold text-foreground">{t(`${rd}.sidebar.details`)}</h2>
                    <div className="text-sm text-muted-foreground">
                      {t(`${rd}.identifier`)}:{' '}
                      <code className="font-mono text-foreground/80">{role.name}</code>
                    </div>
                    <div className="space-y-2 rounded-lg border border-border bg-card p-4">
                      <FormField id="role-description" label={t(`${rd}.description.label`)}>
                        {(fp) => (
                          <Input
                            {...fp}
                            value={description}
                            onChange={(e) => { setDescription(e.target.value); }}
                            autoComplete="off"
                          />
                        )}
                      </FormField>
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            updateRole.mutate(
                              { tenantUuid: uuid, guid, name: role.name, description: description.trim() },
                              {
                                onSuccess: () => {
                                  toast({ title: t(`${rd}.description.saved`), variant: 'success' });
                                },
                              },
                            );
                          }}
                          loading={updateRole.isPending}
                          disabled={description.trim() === role.description}
                        >
                          {t(`${rd}.description.save`)}
                        </Button>
                      </div>
                    </div>
                  </section>
                )}

                {activeSection === 'reference' && (
                  <section className="max-w-3xl space-y-3">
                    <h2 className="text-sm font-semibold text-foreground">{t(`${rd}.reference.heading`)}</h2>
                    <div className="rounded-lg border border-border bg-card p-4">
                      {vocabularyQuery.isPending && <Skeleton className="h-64 w-full rounded-md" aria-hidden="true" />}
                      {vocabularyQuery.isSuccess && (
                        <PermissionVocabularyReference vocabulary={vocabularyQuery.data} />
                      )}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
